import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { formatDistanceToNow } from "date-fns";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function generateStaticParamsGlobal() {
  return Array.from({ length: 10 }).map((_, index) => ({ id: `${index}` }));
}

export const getTimeAgoString = (timestamp: number | string) => {
  if (!timestamp) return "Unknown";
  return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
};

export const getFirstLetter = (name?: string) => {
  return name ? Array.from(name)[0] : "-";
};

export const getColorBasedOnName = (name?: string) => {
  if (!name) {
    return "hsl(var(--muted))";
  }

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash); // Simple hash function
  }

  // Generate HSL color based on the hash with toned-down saturation and lightness
  const color = `hsl(${hash % 360}, 50%, 40%)`; // 50% saturation and 40% lightness for less brightness
  return color;
};

export const isColorDark = (hsl?: string): boolean => {
  if (!hsl || hsl === "hsl(var(--muted))") return false;
  const [h, s, l] = hsl.match(/\d+/g)!.map(Number); // Extract H, S, L values from the HSL string

  // Formula to calculate perceived luminance
  const luminance = l / 100; // Simplified for HSL, lightness directly gives a good idea
  return luminance < 0.5; // If luminance is less than 50%, consider it a "dark" color
};

export const secondsToTime = (seconds: number) => {
  const date = new Date(seconds * 1000).toISOString().slice(11, 19); // Replaced substr with slice
  const [hours, minutes, secondsPart] = date.split(":");

  if (hours === "00") {
    return `${minutes}:${secondsPart}`;
  }

  return `${hours}:${minutes}:${secondsPart}`;
};

// Matches a hostname that is either a dotted name with an alphabetic TLD of 2+
// characters, or a dotted quad. Anchored, no nested quantifiers, so it cannot
// backtrack exponentially.
const HOSTNAME_PATTERN =
  /^(?:[a-z\d](?:[a-z\d-]*[a-z\d])?\.)+[a-z]{2,}$|^(?:\d{1,3}\.){3}\d{1,3}$/i;

/**
 * Validates a URL, with the scheme optional so "example.com/foo" is accepted.
 *
 * Parsing is delegated to the platform `URL` constructor rather than done with a
 * single big regex. The previous implementation matched the whole URL with a
 * pattern containing nested quantifiers — `([a-z\d-]*[a-z\d])*` inside `(…)+`,
 * and `(\/[…]*)*` — which backtracks exponentially on a crafted string
 * (CWE-1333, `typescript:S5852`). That mattered here because this is called on
 * a user-pasted value, on every render of the meeting-URL field, so a pasted
 * string could hang the tab.
 *
 * `URL` handles the structural parsing; the only regex left is applied to the
 * parsed hostname, is anchored, and has no nested quantifiers.
 */
export const isValidURL = (str?: string) => {
  if (!str) return false;
  // A bare "example.com" is not a valid URL to the parser, so add a scheme when
  // one is absent. Reject anything carrying a different scheme outright.
  //
  // Requiring "://" rather than just ":" matters: a scheme may legally contain
  // dots, so `^[a-z][a-z\d+.-]*:` also matches the host in "example.com:3000"
  // and would wrongly reject it as a non-http scheme.
  const hasScheme = /^[a-z][a-z\d+.-]*:\/\//i.test(str);
  if (hasScheme && !/^https?:\/\//i.test(str)) return false;

  try {
    const { hostname, port } = new URL(hasScheme ? str : `https://${str}`);
    if (port && !/^\d+$/.test(port)) return false;
    return HOSTNAME_PATTERN.test(hostname);
  } catch {
    return false;
  }
};

export const isUUID = (str: string): boolean => {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

export const capitalizeFirstLetter = (str: string): string => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const generateChecksum = async (data: Uint8Array): Promise<string> => {
  const hashBuffer = await crypto.subtle.digest("SHA-256", data.buffer as ArrayBuffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
};

export const generateChecksumBlob = async (blob: Blob): Promise<string> => {
  const buffer = await blob.arrayBuffer();
  const data = new Uint8Array(buffer);
  return generateChecksum(data);
};


export const formatTime = (time: number) => {
  const hours = Math.floor(time / 3600);
  const minutes = Math.floor((time % 3600) / 60);
  const seconds = time % 60;

  if (hours > 0) {
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }

  return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};
